import type {
  HomeLayout,
  HomeWidget,
  HomeWidgetDescriptor,
} from "../../api/hub/home";
export type LayoutAction =
  | "left"
  | "right"
  | "up"
  | "down"
  | "narrow"
  | "wide"
  | "short"
  | "tall";
export function copyLayout(layout: HomeLayout): HomeLayout {
  return JSON.parse(JSON.stringify(layout)) as HomeLayout;
}
export function sortedWidgets(layout: HomeLayout) {
  return [...layout.widgets].sort((a, b) =>
    a.y - b.y || a.x - b.x || a.id.localeCompare(b.id)
  );
}
export function overlaps(a: HomeWidget, b: HomeWidget) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h &&
    a.y + a.h > b.y;
}
/** Keep the selected widget at the requested position; settle other widgets downwards in reading order. */
function settle(layout: HomeLayout, id: string) {
  const changed = layout.widgets.find((widget) => widget.id === id);
  if (!changed) return layout;
  const placed = [changed],
    others = sortedWidgets(layout).filter((widget) => widget.id !== id);
  for (const widget of others) {
    let blockers = placed.filter((other) => overlaps(widget, other)), tries = 0;
    while (blockers.length && tries++ < 24) {
      widget.y = Math.max(...blockers.map((other) => other.y + other.h));
      blockers = placed.filter((other) => overlaps(widget, other));
    }
    if (widget.y + widget.h > 1000) {
      throw new Error("移动后超出 1000 行，请先移除或缩小部件");
    }
    placed.push(widget);
  }
  return layout;
}
export function placeWidget(
  layout: HomeLayout,
  id: string,
  position: Partial<Pick<HomeWidget, "x" | "y" | "w" | "h">>,
  descriptors: HomeWidgetDescriptor[],
) {
  const next = copyLayout(layout),
    widget = next.widgets.find((widget) => widget.id === id);
  if (!widget) return next;
  const descriptor = descriptors.find((item) => item.type === widget.type);
  if (!descriptor) throw new Error("该部件已不可用，请移除后再保存");
  Object.assign(widget, position);
  widget.w = Math.max(descriptor.minW, Math.min(descriptor.maxW, widget.w, 12));
  widget.h = Math.max(descriptor.minH, Math.min(descriptor.maxH, widget.h));
  widget.x = Math.max(0, Math.min(12 - widget.w, Math.round(widget.x)));
  widget.y = Math.max(0, Math.min(1000 - widget.h, Math.round(widget.y)));
  return settle(next, id);
}
export function adjustWidget(
  layout: HomeLayout,
  id: string,
  action: LayoutAction,
  descriptors: HomeWidgetDescriptor[],
) {
  const widget = layout.widgets.find((widget) => widget.id === id);
  if (!widget) return layout;
  const updates: Record<LayoutAction, Partial<HomeWidget>> = {
    left: { x: widget.x - 1 },
    right: { x: widget.x + 1 },
    up: { y: widget.y - 1 },
    down: { y: widget.y + 1 },
    narrow: { w: widget.w - 1 },
    wide: { w: widget.w + 1 },
    short: { h: widget.h - 1 },
    tall: { h: widget.h + 1 },
  };
  return placeWidget(layout, id, updates[action], descriptors);
}
const preferredSizes: Record<string, [number, number]> = {
  "native:focus-today": [8, 7],
  "native:okr-mine": [4, 7],
  "native:kb-search": [6, 3],
  "native:quick-links": [4, 3],
  "native:text-note": [4, 4],
  "native:ai-actions-recent": [5, 5],
  "native:approvals-pending": [5, 5],
};
export function addWidget(
  layout: HomeLayout,
  descriptor: HomeWidgetDescriptor,
): HomeLayout {
  if (layout.widgets.length >= 24) {
    throw new Error("最多放 24 个部件，请先移除一个");
  }
  const [preferredW, preferredH] = preferredSizes[descriptor.type] ?? [6, 5];
  const y = Math.max(0, ...layout.widgets.map((widget) => widget.y + widget.h)),
    h = Math.min(descriptor.maxH, Math.max(descriptor.minH, preferredH));
  if (y + h > 1000) throw new Error("布局超出 1000 行，请先缩小或移除部件");
  return {
    ...layout,
    widgets: [...layout.widgets, {
      id: "w_" + Date.now().toString(36) + "_" +
        Math.random().toString(36).slice(2, 8),
      type: descriptor.type,
      x: 0,
      y,
      w: Math.min(12, descriptor.maxW, Math.max(descriptor.minW, preferredW)),
      h,
      config: {},
    }],
  };
}
export function localLayoutError(
  layout: HomeLayout,
  descriptors: HomeWidgetDescriptor[],
) {
  if (layout.schema !== "home/1" || layout.cols !== 12) {
    return "布局必须使用 home/1 与 12 列";
  }
  if (new TextEncoder().encode(JSON.stringify(layout)).byteLength > 65536) {
    return "布局最大 64 KiB，请缩短配置文字或减少部件";
  }
  if (layout.widgets.length > 24) return "最多 24 个部件";
  for (let i = 0; i < layout.widgets.length; i++) {
    const widget = layout.widgets[i],
      descriptor = descriptors.find((item) => item.type === widget.type);
    if (!descriptor) return "「" + widget.id + "」已不可用，请移除后保存";
    if (
      !/^[A-Za-z0-9_-]{1,100}$/.test(widget.id) ||
      layout.widgets.slice(0, i).some((other) => other.id === widget.id)
    ) return "部件编号不正确或重复，请重新添加";
    if (
      ![widget.x, widget.y, widget.w, widget.h].every(Number.isInteger) ||
      widget.x < 0 || widget.y < 0 || widget.x + widget.w > 12 ||
      widget.y + widget.h > 1000
    ) return "部件位置超出 12 列与 1000 行，请调整";
    if (
      widget.w < descriptor.minW || widget.w > descriptor.maxW ||
      widget.h < descriptor.minH || widget.h > descriptor.maxH
    ) return "「" + descriptor.name + "」尺寸不在允许范围，请调整";
    if (layout.widgets.slice(0, i).some((other) => overlaps(widget, other))) {
      return "部件发生重叠，请移动后保存";
    }
  }
  return null;
}
